import { useEffect, useState } from 'react';

import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import { getFormDefaultValue, getTableHeaders } from '../../utils/forms/FormDataModel';
import { ContentItem, OnAddArguments, TabContent } from '../../utils/forms/FormInterface';
import AbstractForm from '../Entity/AbstractForm';
import AbstractLanding from '../Entity/AbstractLanding';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import { BUCKET_MANAGEMENT } from '../../constants/EntityName';
import {
  bucketFormData,
  fetchAndSetOptions,
  fetchBucketServiceOptions,
} from '../../datamodels/Buckets';
import { InputInterface } from '../../utils/forms/FormInterface';

const BucketManagement = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const [formTemplate, setFormTemplate] = useState<InputInterface[]>(bucketFormData);

  const defaultFormValue: any = getFormDefaultValue(formTemplate);
  const tableHeaders: any = getTableHeaders(formTemplate);
  const addName = 'Bucket';
  const headerName = 'Bucket List';
  const formTemplateKey = formTemplate
    .find(field => field.name === 'service')
    ?.fieldType?.options?.map((option: any) => option.value)
    .join('-');

  // Debug log the form template
  useEffect(() => {
    console.log('BucketManagement: Updated form template:', formTemplate);
  }, [formTemplate]);

  // Fetch admin users and services when component mounts
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [adminUsers, services] = await Promise.all([
          fetchAndSetOptions(),
          fetchBucketServiceOptions(),
        ]);

        if ((adminUsers && adminUsers.length > 0) || (services && services.length > 0)) {
          const updatedFormTemplate: InputInterface[] = bucketFormData.map(field => {
            if (field.name === 'adminusers') {
              return {
                ...field,
                fieldType: {
                  ...field.fieldType,
                  options: adminUsers,
                },
              };
            }
            if (field.name === 'service') {
              return {
                ...field,
                fieldType: {
                  ...field.fieldType,
                  options: services,
                },
              };
            }
            return field;
          });

          setFormTemplate(updatedFormTemplate);
        }
      } catch (error) {
        console.error('Error loading bucket form options:', error);
      }
    };

    loadOptions();
  }, []);

  const onSuccessful = (id?: string) => {
    console.log('BucketManagement: onSuccessful called with id:', id);
    if (id === 'create') {
      setShowFormComponent(false);
    }
    onTabClick('0');
    setRefreshData(prev => !prev);
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const checkArr = content.filter((item: ContentItem) => item.key === argument1.id);
      checkArr.length ? null : onAdd(argument1);
    } else {
      setShowFormComponent(true);
      setUserName('Add Bucket');
      onTabClick('a');
    }
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    key === 'a' && setShowFormComponent(false);
  };

  const onAdd = (argument1: OnAddArguments) => {
    const newObj = {
      key: argument1.id,
      tabTitle: argument1.id,
      content: (
        <GenericAccordian key={content.length}>
          <AbstractForm
            entityId={argument1.id}
            isCreateMode={false}
            headerName={headerName}
            entityName={BUCKET_MANAGEMENT}
            formTemplate={formTemplate}
            formDefaultPayload={getFormDefaultValue(formTemplate)}
            onSuccessfulUpdate={onSuccessful}
            formHeader=""
            showDeleteDraft={true}
            closeTab={closeTab}
          />
        </GenericAccordian>
      ),
    };

    // It is to make the newly added tab as active
    setContent((prev: any) => [...prev, newObj]);
    argument1.id && setActiveKey(String(argument1.id));
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        onTabClick('0');
      } else {
        onTabClick('0');
        // whenever a tab is closed, that tab is filtered out from the list of tabs which are rendered inside the AbstractTab component
        setContent(content => content.filter(item => String(item.key) !== targetKey));
      }
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Bucket Management"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent}
      content={content}
      formComponent={
        <GenericAccordian>
          <AbstractForm
            key={`create-${formTemplateKey}`}
            isCreateMode={true}
            headerName={headerName}
            entityName={BUCKET_MANAGEMENT}
            formTemplate={formTemplate}
            formDefaultPayload={defaultFormValue}
            onSuccessfulUpdate={onSuccessful}
            closeTab={closeTab}
          />
        </GenericAccordian>
      }
    >
      <AbstractLanding
        headerName={headerName}
        addName={addName}
        entityName={BUCKET_MANAGEMENT}
        tableHeaders={tableHeaders}
        onAdd={onAddNew}
        refreshData={refreshData}
      />
    </AbstractTabs>
  );
};

export default BucketManagement;
